import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const MyFormations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFormations, setTotalFormations] = useState(0);
  const formationsPerPage = 10;

  useEffect(() => {
    fetchAssignments();
  }, [currentPage]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: formationsPerPage
      });
      
      const response = await api.get(`/formations/assigned?${params.toString()}`);
      const data = response.data.data;
      setAssignments(data?.data || data || []);
      setTotalPages(data?.last_page || 1);
      setTotalFormations(data?.total || 0);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (formationId) => {
    navigate(`/formations/${formationId}/view`);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getStatusColor = (status, displayStatus) => {
    // Use display_status if available, otherwise use status
    const statusToCheck = displayStatus || status;
    switch (statusToCheck) {
      case 'completed_online': return 'text-red-600 bg-red-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'absent': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status, displayStatus) => {
    // Use display_status if available, otherwise use status
    const statusToCheck = displayStatus || status;
    switch (statusToCheck) {
      case 'completed_online': return 'Complété en ligne';
      case 'completed': return 'Complété';
      case 'in_progress': return 'En cours';
      case 'absent': return 'Absent';
      default: return 'En attente';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      {/* Header */}
      <header className="relative z-10 p-6 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center sm:text-left">
          MES FORMATIONS & BRIEFS
        </h1>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-4 sm:p-6 animate-slide-up">
        <div className="max-w-7xl mx-auto">
          {/* Formations Table */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
            {loading ? (
              <div className="p-6 sm:p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-sm sm:text-base text-gray-600">Chargement des formations...</p>
              </div>
            ) : assignments.length === 0 ? (
              <div className="p-6 sm:p-8 text-center">
                <p className="text-gray-600">Aucune formation ou brief assigné</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Titre
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Survey
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigné le
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date limite
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {assignments.map((assignment) => (
                        <tr
                          key={assignment.id}
                          onClick={() => handleView(assignment.formation_id)}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {assignment.formation?.title || 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm text-gray-900">
                              {assignment.formation?.survey?.title || 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm text-gray-900">
                              {assignment.assigned_at ? new Date(assignment.assigned_at).toLocaleDateString('fr-FR') : 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm text-gray-900">
                              {assignment.due_date ? new Date(assignment.due_date).toLocaleDateString('fr-FR') : 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(assignment.status, assignment.display_status)}`}>
                              {getStatusText(assignment.status, assignment.display_status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-200">
                  {assignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      onClick={() => handleView(assignment.formation_id)}
                      className="p-4 sm:p-5 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="mb-3">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 break-words">
                          {assignment.formation?.title || 'N/A'}
                        </h3>
                        {assignment.formation?.description && (
                          <p className="text-xs sm:text-sm text-gray-600 mb-3 line-clamp-2 break-words">
                            {assignment.formation.description}
                          </p>
                        )}
                      </div>
                      
                      <div className="mb-3 space-y-2">
                        <div className="text-xs sm:text-sm text-gray-700">
                          <span className="font-medium">Survey:</span> {assignment.formation?.survey?.title || 'N/A'}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-700">
                          <span className="font-medium">Assigné le:</span> {assignment.assigned_at ? new Date(assignment.assigned_at).toLocaleDateString('fr-FR') : 'N/A'}
                        </div>
                        {assignment.due_date && (
                          <div className="text-xs sm:text-sm text-gray-700">
                            <span className="font-medium">Date limite:</span> {new Date(assignment.due_date).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(assignment.status, assignment.display_status)}`}>
                            {getStatusText(assignment.status, assignment.display_status)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Affichage de {(currentPage - 1) * formationsPerPage + 1} à {Math.min(currentPage * formationsPerPage, totalFormations)} sur {totalFormations} formations
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {currentPage} sur {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyFormations;

